<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';
import { useElementSize } from '@vueuse/core';
import ListGroupHeader from '@/components/DataTable/ListGroupHeader.vue';

/**
 * Apple Mail-style master-detail layout for the search page's Cards view,
 * generalized from {@link file:src/components/AudioFileIssues/AudioFileIssueSplitView.vue}:
 * a scrollable master list in the left pane (rows rendered by the parent via
 * the #row slot), a pointer-draggable divider, and a right pane whose content
 * the parent provides via the #detail slot.
 *
 * The list pane is the one with a set width, and that width is saved, so it
 * opens the way it was left. It starts at its minimum, 320 pixels, and the
 * detail pane takes the rest. It used to be the other way round: the detail
 * pane's width was saved, so on a wider window, or with the section sidebar
 * closed, the list grew and had to be dragged back every time (Joel,
 * 2026-10-17).
 *
 * The list also keeps its width when the window resizes. The detail pane takes
 * the difference until it reaches its own minimum. Only then does the list give
 * way, and never below its 320-pixel minimum.
 *
 * Mobile fall-through stays in the parent — this component is only mounted on
 * viewports wide enough for both panes.
 */
const props = defineProps<{
    rows: Record<string, unknown>[];
    /** Row index → group header label, when the list is grouped. */
    groupHeaders?: Map<number, string>;
    hasSelection: boolean;
    /** True while the upstream list query is loading; used for the empty-state copy. */
    isLoading?: boolean;
}>();

// A new key: the old one held the detail pane's width, which means something else.
const STORAGE_KEY = 'search-cards-split-list-width';
const MIN_LIST_WIDTH = 320;
const MIN_DETAIL_WIDTH = 320;
/** The divider is w-1.5. */
const DIVIDER_WIDTH = 6;

/** The saved list width, or null when there is none or it cannot be read. */
function readStoredListWidth(): number | null {
    if (typeof localStorage === 'undefined') return null;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        const parsed = Number(stored);
        return Number.isFinite(parsed) && parsed >= MIN_LIST_WIDTH ? parsed : null;
    } catch {
        return null;
    }
}

const listWidth = ref(readStoredListWidth() ?? MIN_LIST_WIDTH);
const containerRef = ref<HTMLElement | null>(null);
// Reactive, so a window resize re-clamps the list. A DOM width read inside the
// computed would not be, because Vue cannot watch it.
const { width: containerWidth } = useElementSize(containerRef);
const isResizing = ref(false);

/** At least the list's minimum, and never so wide the detail pane drops below its own. */
function clampToContainer(candidate: number): number {
    const atLeastMinimum = Math.max(MIN_LIST_WIDTH, candidate);
    if (containerWidth.value <= 0) {
        return atLeastMinimum;
    }
    const ceiling = Math.max(MIN_LIST_WIDTH, containerWidth.value - DIVIDER_WIDTH - MIN_DETAIL_WIDTH);
    return Math.min(ceiling, atLeastMinimum);
}

const effectiveListWidth = computed(() => clampToContainer(listWidth.value));

let resizeStartX = 0;
let resizeStartWidth = 0;

function onResizeStart(event: PointerEvent) {
    event.preventDefault();
    isResizing.value = true;
    resizeStartX = event.clientX;
    resizeStartWidth = effectiveListWidth.value;
    document.addEventListener('pointermove', onResizeMove);
    document.addEventListener('pointerup', onResizeEnd);
}

function onResizeMove(event: PointerEvent) {
    if (!isResizing.value) return;
    // Dragging the divider right widens the list.
    const delta = event.clientX - resizeStartX;
    listWidth.value = clampToContainer(resizeStartWidth + delta);
}

function onResizeEnd() {
    if (!isResizing.value) return;
    isResizing.value = false;
    if (typeof localStorage !== 'undefined') {
        try {
            localStorage.setItem(STORAGE_KEY, String(Math.round(listWidth.value)));
        } catch {
            // Storage may be unavailable — best-effort only.
        }
    }
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
}

onBeforeUnmount(() => {
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
});

watchEffect(() => {
    if (typeof document === 'undefined') return;
    if (isResizing.value) {
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    } else {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }
});

const showEmptyState = computed(() => !props.isLoading && props.rows.length === 0);
</script>

<template>
    <div ref="containerRef" data-testid="search-cards-split-view" class="flex h-full w-full min-h-0">
        <!-- Left pane: scrollable master list. min-w-0 so flex-basis can shrink past content width.
             `relative`, so the pane is the containing block for anything absolutely
             positioned inside a row (an `sr-only` label is) and its overflow clips it.
             A scroll container that is not positioned does not clip those: they belong
             to the layout's content region instead, and the ones in rows scrolled out
             of view sat below the viewport and made the whole page scroll. -->
        <div
            data-testid="search-cards-split-list"
            class="relative flex min-h-0 shrink-0 flex-col overflow-auto"
            :style="{ width: `${effectiveListWidth}px` }"
        >
            <div class="flex w-full flex-col gap-2 px-3 pt-2 pb-4">
                <template v-for="(row, index) in rows" :key="String(row.id)">
                    <ListGroupHeader v-if="groupHeaders?.has(index)" :label="groupHeaders.get(index)!" />
                    <slot name="row" :row="row" />
                </template>
                <div v-if="showEmptyState" class="text-muted-foreground py-8 text-center text-sm">
                    No results match the current filters.
                </div>
            </div>
        </div>

        <!-- Resize handle. Pointer-only; keyboard users can switch to table mode. -->
        <div
            data-testid="search-cards-split-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize detail pane"
            class="relative w-1.5 shrink-0 cursor-col-resize bg-border/60 hover:bg-primary/40 active:bg-primary/60 transition-colors"
            @pointerdown="onResizeStart"
        />

        <!-- Right pane: inline inspector for the selected row. It takes whatever the list
             leaves, so a window resize lands here first. `relative` for the same
             reason as the list pane: the inspector's own hidden labels stay inside it. -->
        <div
            data-testid="search-cards-split-detail"
            class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-auto border-l bg-background"
        >
            <!-- Flex column at least as tall as the pane so an inspector that
                 flex-fills (e.g. the player inspector's Logs card) has a height
                 chain to grow into; taller content still scrolls the pane. -->
            <div v-if="hasSelection" class="flex min-h-full flex-col px-5 py-6">
                <slot name="detail" />
            </div>
            <div
                v-else
                data-testid="search-cards-split-detail-empty"
                class="text-muted-foreground flex flex-1 items-center justify-center px-5 py-6 text-center text-sm"
            >
                Select a row to inspect it.
            </div>
        </div>
    </div>
</template>
